import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Collapse, IconButton, Paper, Stack, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  getMarketResearchEditActivity,
  type MarketResearchEditActivityEntry,
} from '../api/marketResearchEditAwareness';
import { isMarketResearchHandoffAvailable, type MarketResearchHandoffState } from '../api/marketResearchHandoffConversation';

const emptyActivityMessage = 'No activity has been recorded after handoff';
const notHandedOffMessage = 'Not yet handed off, so no activity recorded';
const genericError = 'We could not load workflow updates. Please try again.';

const formatTimestamp = (value: string): string => new Intl.DateTimeFormat(undefined, {
  year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
}).format(new Date(value));

const relativeTimestamp = (value: string): string => {
  const milliseconds = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(milliseconds / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export interface MarketResearchEditActivityProps {
  flowId: string;
  isSampleFlow?: boolean;
  /** The existing handoff/chat resolver gate. A false gate must not fetch. */
  handoffState?: MarketResearchHandoffState;
  /** Existing authorized route mapping; no row invents a stage navigation. */
  onNavigate?: (backendStageCode: number) => boolean | void;
}

/**
 * Safe, generic edit history shown only by the already-authorized collaboration
 * routes. It intentionally offers no field diff or navigation side effect;
 * activity is awareness, not acknowledgement.
 */
export const MarketResearchEditActivity: React.FC<MarketResearchEditActivityProps> = ({
  flowId,
  handoffState,
  isSampleFlow = false,
  onNavigate,
}) => {
  const [entries, setEntries] = useState<MarketResearchEditActivityEntry[]>([]);
  const [earlierCursor, setEarlierCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const request = useRef<{ generation: number; controller: AbortController | null }>({
    generation: 0, controller: null,
  });

  const invalidateRequest = useCallback(() => {
    request.current.generation += 1;
    request.current.controller?.abort();
    request.current.controller = null;
  }, []);

  const canLoad = !isSampleFlow && (handoffState === undefined || isMarketResearchHandoffAvailable(handoffState));
  const emptyMessage = handoffState === 'not_handed_off' ? notHandedOffMessage : emptyActivityMessage;

  const load = useCallback(async (before?: string | null) => {
    if (!canLoad) return;
    invalidateRequest();
    const generation = request.current.generation;
    const controller = new AbortController();
    request.current.controller = controller;
    setLoading(true);
    setError(false);
    try {
      const page = await getMarketResearchEditActivity(flowId, before, controller.signal);
      if (request.current.generation !== generation || controller.signal.aborted) return;
      setEntries((current) => {
        if (!before) return page.entries;
        const known = new Set(current.map((entry) => entry.event_id));
        return [...current, ...page.entries.filter((entry) => !known.has(entry.event_id))];
      });
      setEarlierCursor(page.earlier_cursor);
    } catch (loadError) {
      if (request.current.generation !== generation || controller.signal.aborted) return;
      // Safe diagnostics for an actual current failure; never include a
      // response payload, workflow data, or principal details in the UI.
      console.warn('Market Research Activity request failed', {
        name: loadError instanceof Error ? loadError.name : 'unknown',
      });
      setError(true);
    } finally {
      if (request.current.generation === generation && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [flowId, canLoad, invalidateRequest]);

  useEffect(() => {
    if (!canLoad) {
      invalidateRequest();
      setEntries([]);
      setEarlierCursor(null);
      setLoading(false);
      setError(false);
      return;
    }
    // A different authorized flow is a new panel view, so preserve the
    // existing visible-by-default behavior without keying/unmounting its body
    // during ordinary collapse/expand interactions.
    setExpanded(true);
    setEntries([]);
    setEarlierCursor(null);
    void load();
    return invalidateRequest;
  }, [flowId, canLoad, invalidateRequest, load]);

  useEffect(() => {
    // `inert` is intentionally set as a DOM attribute because this project’s
    // React DOM type version predates the inert property. It prevents keyboard
    // focus and synthetic interaction with the retained collapsed subtree.
    if (expanded) contentRef.current?.removeAttribute('inert');
    else contentRef.current?.setAttribute('inert', '');
  }, [expanded]);


  const contentId = `market-research-edit-activity-content-${flowId}`;
  const toggle = () => {
    if (expanded && contentRef.current?.contains(document.activeElement)) {
      toggleRef.current?.focus();
    }
    setExpanded((current) => !current);
  };

  return (
    <Paper
      component="section"
      aria-labelledby="market-research-edit-activity-title"
      data-testid="market-research-edit-activity"
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2 }, borderColor: 'divider', backgroundColor: 'background.paper' }}
    >
      <Stack spacing={1.25}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: 'space-between', gap: 1 }}>
          <Typography component="h2" id="market-research-edit-activity-title" sx={{ minWidth: 0 }} variant="h6">
            Activity
          </Typography>
          {canLoad && <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
            <Button
              aria-label="Refresh activity"
              disabled={loading}
              onClick={() => void load()}
              size="small"
              startIcon={loading ? <CircularProgress color="inherit" size={14} /> : <RefreshIcon />}
              variant="text"
            >
              Refresh
            </Button>
            <IconButton
              aria-controls={contentId}
              aria-expanded={expanded}
              aria-label={expanded ? 'Collapse Activity' : 'Expand Activity'}
              onClick={toggle}
              ref={toggleRef}
              size="small"
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>}
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
          <Box
            aria-hidden={!expanded}
            id={contentId}
            ref={contentRef}
          >
            <Stack spacing={1.25}>
        {error && (
          <Alert
            action={<Button color="inherit" onClick={() => void load()} size="small">Retry activity</Button>}
            severity="error"
          >
            {genericError}
          </Alert>
        )}

        {!canLoad ? (<Typography color="text.secondary" variant="body2">{emptyMessage}</Typography>) : loading && entries.length === 0 ? (
          <Box aria-live="polite" data-testid="market-research-edit-activity-loading" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading workflow updates</Typography>
          </Box>
        ) : entries.length === 0 && !error ? (
          <Typography color="text.secondary" variant="body2">{emptyMessage}</Typography>
        ) : (
          <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {entries.map((entry) => {
              const canNavigate = Boolean(onNavigate);
              const content = <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {entry.stage_label} — {entry.summary}
                </Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mt: 0.25 }}>
                  {entry.actor} · <time dateTime={entry.occurred_at} title={formatTimestamp(entry.occurred_at)}>
                    {formatTimestamp(entry.occurred_at)} ({relativeTimestamp(entry.occurred_at)})
                  </time>
                </Typography>
              </>;
              return (
              <Box
                component="li"
                key={entry.event_id}
                sx={{ borderTop: '1px solid', borderColor: 'divider', py: 1.25 }}
              >
                {canNavigate ? (
                  <Button
                    aria-label={`Open ${entry.stage_label} update`}
                    onClick={() => { onNavigate?.(entry.stage_code); }}
                    sx={{ alignItems: 'flex-start', color: 'inherit', display: 'block', p: 0, textAlign: 'left', textTransform: 'none' }}
                    variant="text"
                  >
                    {content}
                  </Button>
                ) : content}
              </Box>
              );
            })}
          </Box>
        )}

        {earlierCursor && (
          <Box>
            <Button disabled={loading} onClick={() => void load(earlierCursor)} size="small">
              Show earlier activity
            </Button>
          </Box>
        )}
            </Stack>
          </Box>
        </Collapse>
      </Stack>
    </Paper>
  );
};
